import React, { Dispatch } from "react";
import { useTheme } from "react-jss";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames";
import { includes, times } from "../../../utils/lodash";
import { FontAwesomeIcon } from "../../shared/FontAwesome";
import {
    shouldShowRowLeftLabel,
    isHeaderRow,
    shouldShowRowRightLabel,
    questionForCell,
    isSplitColumn,
    isRowMergeCells,
    getUseChoiceTexts,
    variableForCell,
    getColumnCount,
    openEndVariableName,
    hasRightLabels,
    hasLeftLabels,
    rowHasError,
    hasOpenEnd,
} from "../../../models/utilities/gridUtilities";
import {
    getSettingString,
    getSettingBoolean,
    hasSetting,
    getSettingNumber,
} from "../../../models/utilities/settingUtilities";
import { getRendererProps, RendererProps } from "../BaseRenderer";
import GridCellRenderer from "./GridCellRenderer";
import OpenEndRenderer from "./OpenEndRenderer";
import { ChoiceContainer } from "../../../containers/controls/ChoiceContainer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { QuestionType } from "../../../models/enums/QuestionType";
import { SignalListener } from "../../shared/SignalListener";
import { isNullOrEmpty } from "../../../utils";
import { RowType } from "../../../models/enums/RowType";
import { controlTypeForColumn } from "../../../models/factories/gridControlFactory";
import { VariableModel } from "../../../models/VariableModel";
import { AxisModel } from "../../../models/AxisModel";
import { translate } from "../../../models/utilities/translationUtilities";
import { ControlContainer } from "../../../containers/controls/ControlContainer";
import { Signal } from "../../../signals";
import { ValidationResultModel } from "../../../models/ValidationResultModel";
import { ValidationOptionsModel } from "../../../models/ValidationOptionsModel";
import { ColumnType } from "../../../models/enums/ColumnType";
import { Collapsible } from "../../shared/Collapsible";
import useStyle from "./GridRenderer.styles";
import Button from "../../shared/Button";
import useQuestion, { QuestionContext } from "../../../hooks/useQuestion";
import useTranslatable from "../../../hooks/useTranslatable";
import useRecall from "../../../hooks/useRecall";
import ChoiceListItemRenderer from "./ChoiceListItemRenderer";
import { defaultSize } from "../../../models/utilities/questionUtilities";
import InputWrapper from "../../shared/InputWrapper";
import { Translatable } from "../../../models/Translatable";
import { evaluateIfChoiceVisible } from "../../../redux/thunks/evaluator";
import { useMentions } from "../../../hooks/useMentions";
import { MentionModel } from "../../../models/MentionModel";
import { StoreState } from "../../../models/states/StoreState";
import { getMentions } from "../../../redux/reducers/interview/variablesUtilities";
import { setQuestionFieldStatus, setVariableMentions } from "../../../redux/actions";

interface GridRendererProps extends RendererProps {
    rows: AxisModel[];
    columns: AxisModel[];
    validateSignal: Signal<ValidationOptionsModel, ValidationResultModel>;
    validationResult: ValidationResultModel;
    getSelectedAnswers?(row: AxisModel, column: AxisModel): ChoiceModel[];
    shouldHideCell?(row: AxisModel, column: AxisModel): boolean;
    shouldDisableCell?(row: AxisModel, column: AxisModel): boolean;
    validateCell?(row: AxisModel, column: AxisModel): ValidationResultModel;
    validateColumn?(column: AxisModel): ValidationResultModel;
    validateRowOpenEnd?(row: AxisModel): ValidationResultModel;
    validateChoiceOpenEnd?(column: AxisModel, choice: ChoiceModel): ValidationResultModel;
    getColumnRunningTotal?(column: AxisModel): string;
    isRowSelected?(row: AxisModel): boolean;
    expandRow?(row: AxisModel): void;
    addAccordionRef?(rowIndex: number, ref: HTMLDivElement): void;
    openEndValue?: { [key: string]: string };
    onOpenEndChange?(answer: any, event: any): void;
    onOpenEndBlur?(answer: any): void;
    showSoftPrompt?: boolean;
    matrix?: number;
    onReset?(): void;
    expandedRowsIndices?: number[];
    isScreenSmall?: boolean;
    isColumnChoiceSelected?(column: AxisModel, choice: ChoiceModel): boolean;
    max?: string;
    sequencedVariables?: VariableModel[];
}

interface StyledProps extends GridRendererProps {
    classes: any;
}

interface ReferencedElementProps extends StyledProps {
    elementRef?: React.Ref<HTMLElement>;
}

interface RecalledLabelProps {
    translatable: Translatable;
    translatableKey: string;
    classes?: string;
}

const isTransposedGrid = (props: GridRendererProps) =>
    getSettingBoolean(props.question, "InverseRendering");

const shouldDisplayResetButton = (props: GridRendererProps) =>
    getSettingBoolean(props.question, "DisplayResetButton");

const defaultColumnsWidth = (props: GridRendererProps) =>
    columnsWidth(props, "DefaultColumnsWidth");

const firstColumnWidth = (props: GridRendererProps) => columnsWidth(props, "FirstColumnWidth");

const columnsWidth = (props: GridRendererProps, key: string) => {
    const settingValue = getSettingString(props.question, key);
    return includes(settingValue, "%") ? settingValue : null;
};

function RecalledLabel(props: RecalledLabelProps) {
    const { translatable, translatableKey, classes } = props;
    const label = useTranslatable(translatable, translatableKey);
    const recalled = useRecall(label);

    if (!recalled) {
        return null;
    }

    return <div className={classes} dangerouslySetInnerHTML={{ __html: recalled }} />;
}

function LeftLabel(props: StyledProps & { axis: AxisModel }) {
    const { classes, axis } = props;

    return (
        <RecalledLabel
            translatable={axis}
            translatableKey="LeftLabel"
            classes={classnames(classes.label, classes.labelLeft)}
        />
    );
}

function RightLabel(props: StyledProps & { axis: AxisModel }) {
    const { classes, axis } = props;

    return (
        <RecalledLabel
            translatable={axis}
            translatableKey="RightLabel"
            classes={classnames(classes.label, classes.labelRight)}
        />
    );
}

function Title(props: StyledProps & { row: AxisModel; column: AxisModel }) {
    const { classes, column, row } = props;

    return (
        <RecalledLabel
            translatable={row}
            translatableKey={`Labels_${column.index + 1}`}
            classes={classes.title}
        />
    );
}

function Text(props: StyledProps & { row: AxisModel; column: AxisModel; index: number }) {
    const { column, row, index } = props;

    return (
        <RecalledLabel
            translatable={row}
            translatableKey={`Labels_${column.index + 1}_${index + 1}`}
        />
    );
}

function ChoiceText(props: StyledProps & { choice: ChoiceModel }) {
    const { choice } = props;
    return <RecalledLabel translatable={choice} translatableKey="TEXT" />;
}

function RowOpenEnd(props: GridRendererProps & { row: AxisModel }) {
    const { row } = props;
    const isHidden = row.openEndActive !== undefined ? !row.openEndActive : false;
    const isDisabledIfHidden = getSettingBoolean(row, "DisplayIfDisableInstead");
    const isDisabled = row.openEndDisplayIf && isHidden && isDisabledIfHidden;

    return (
        <OpenEnd
            rowOrChoice={row}
            isHidden={isHidden}
            isDisabledIfHidden={isDisabledIfHidden}
            isDisabled={isDisabled}
            {...props}
        />
    );
}

function ChoiceColumnOpenEnd(
    props: GridRendererProps & { choice: ChoiceModel; column: AxisModel },
) {
    const { choice } = props;
    const isHidden = choice.openEndActive !== undefined ? !choice.openEndActive : false;
    const isDisabledIfHidden = getSettingBoolean(choice, "DisplayIfDisableInstead");
    const isDisabled = choice.openEndDisplayLogic && isHidden && isDisabledIfHidden;

    return (
        <OpenEnd
            rowOrChoice={choice}
            isHidden={isHidden}
            isDisabledIfHidden={isDisabledIfHidden}
            isDisabled={isDisabled}
            {...props}
        />
    );
}

function OpenEnd(
    props: GridRendererProps & {
        rowOrChoice: AxisModel | ChoiceModel;
        isHidden: boolean;
        isDisabledIfHidden: boolean;
        isDisabled: boolean;
    },
) {
    const { rowOrChoice, isHidden, isDisabledIfHidden, isDisabled } = props;
    const leftLabel = useTranslatable(rowOrChoice, "LeftLabel");
    const text = useTranslatable(rowOrChoice, "TEXT");

    if (!hasSetting(rowOrChoice, "OpenEndType")) {
        return null;
    }

    const openEndName = openEndVariableName(rowOrChoice);
    const { validationResult, openEndValue, onOpenEndBlur, onOpenEndChange } = props;

    const size = getSettingString(rowOrChoice, "OpenEndSize", "Medium");
    const lines = getSettingNumber(rowOrChoice, "OpenEndLines", 2);

    return (
        <OpenEndRenderer
            {...getRendererProps(props)}
            name={`V_${openEndName}`}
            label={leftLabel || text}
            isHidden={isHidden}
            value={openEndValue[openEndName]}
            isDisabledRequest={isDisabledIfHidden}
            isDisabled={isDisabled}
            onOpenEndBlur={() => onOpenEndBlur(rowOrChoice)}
            onOpenEndChange={(e) => onOpenEndChange(rowOrChoice, e)}
            validationResult={validationResult}
            size={size}
            lines={lines}
            gridRendering
        />
    );
}

function RowLeftLabel(props: StyledProps & { row: AxisModel }) {
    const { row } = props;
    if (!shouldShowRowLeftLabel(row)) {
        return null;
    }
    const { validationResult, validateSignal } = props;
    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            isColumnHeader={isHeaderRow(row)}
            isRowHeader={!isHeaderRow(row)}
            validationResult={validationResult}
            row={row}
        >
            <LeftLabel {...props} axis={row} />
            <RowOpenEnd row={row} {...props} />
            {hasSetting(row, "OpenEndType") && (
                <SignalListener
                    signal={validateSignal}
                    handler={() => props.validateRowOpenEnd(row)}
                />
            )}
        </GridCellRenderer>
    );
}

function RowRightLabel(props: StyledProps & { row: AxisModel }) {
    const { row } = props;
    if (!shouldShowRowRightLabel(row)) {
        return null;
    }
    const { validationResult } = props;
    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            isColumnHeader={isHeaderRow(row)}
            isRowHeader={!isHeaderRow(row)}
            validationResult={validationResult}
            row={row}
        >
            <RightLabel {...props} axis={row} />
        </GridCellRenderer>
    );
}

function GetQuestionFieldStatus(fieldIdentifier: string) {
    return useSelector((state: StoreState) => state.interview.questionFieldStatus[fieldIdentifier]);
}

function Choice(
    props: StyledProps & {
        row: AxisModel;
        column: AxisModel;
        variable: VariableModel;
        choice: ChoiceModel;
        isCollapsible?: boolean;
        label?: string;
    },
) {
    const {
        question,
        row,
        column,
        variable,
        choice,
        isCollapsible,
        openEndValue,
        onOpenEndChange,
        onOpenEndBlur,
    } = props;
    const [mentions] = useMentions(variable.name);
    const store = useSelector((state: StoreState) => state);
    const dispatch = useDispatch();
    const isSelected = isChoiceSelected(choice, mentions);
    const cellQuestion = questionForCell(question, row, column);
    const fieldIdentifier = `${question.id}&${cellQuestion.name}`;
    const isHybridGrid = question?.type === QuestionType.HybridGrid;
    let isDisabled: boolean;
    let stateDisabledStatus;
    if (isHybridGrid) {
        stateDisabledStatus = GetQuestionFieldStatus(fieldIdentifier)?.isDisabled;

        if (stateDisabledStatus) {
            isDisabled = stateDisabledStatus;
        } else {
            isDisabled = isChoiceDisabled(props, mentions, store, dispatch);
        }
    } else {
        isDisabled = isChoiceDisabled(props, mentions, store, dispatch);
    }

    React.useEffect(() => {
        if (isHybridGrid && stateDisabledStatus === undefined) {
            dispatch(setQuestionFieldStatus([fieldIdentifier], isDisabled));
        }
    }, []); // Runs once per component(Grid Cell)

    const choiceRef = React.useRef();

    const isRadioOrCheckbox = [ColumnType.RadioButton, ColumnType.CheckBox].includes(
        column.type as ColumnType,
    );

    const applyCollapsibleStyles = isCollapsible && isRadioOrCheckbox;
    if (!applyCollapsibleStyles) {
        return (
            <div key={choice.index}>
                <ChoiceContainer
                    question={questionForCell(question, row, column)}
                    gridRendering={!isCollapsible}
                    choice={choice}
                    choiceRef={choiceRef}
                    controlType={controlTypeForColumn(column)}
                    isSelected={isSelected}
                    isDisabled={isDisabled}
                    variable={variable}
                    executeUpdateForIdAfterSelection={fieldIdentifier}
                />
            </div>
        );
    }

    return (
        <ChoiceListItemRenderer
            {...getRendererProps(props)}
            controlType={controlTypeForColumn(column)}
            gridRendering={!isCollapsible}
            openEnd={(openEndValue || {})[choice.index]}
            onOpenEndChange={onOpenEndChange}
            onOpenEndBlur={onOpenEndBlur}
            selected={isSelected}
            choice={choice}
            variable={variable}
            disabled={isDisabled}
        />
    );
}

function ChoiceCells(props: StyledProps & { row: AxisModel; column: AxisModel }) {
    const { row, column, validateSignal, question } = props;
    const variable = variableForCell(row, column);
    const colSpan = question.variables?.find((v) => v.name === variable.name).choiceList?.length;
    return (
        <>
            <SignalListener
                key="signal"
                handler={() => props.validateCell(row, column)}
                signal={validateSignal}
            />
            {variable.choiceList.length > 0 ? (
                variable.choiceList.map((choice) => (
                    <GridCellRenderer
                        {...getRendererProps(props)}
                        key={choice.elementId}
                        validationResult={props.validationResult}
                        row={row}
                        column={column}
                        choice={choice}
                    >
                        <Choice
                            row={row}
                            column={column}
                            variable={variable}
                            choice={choice}
                            {...props}
                        />
                    </GridCellRenderer>
                ))
            ) : (
                <td colSpan={colSpan} />
            )}
        </>
    );
}

function VariableCell(props: StyledProps & { row: AxisModel; column: AxisModel }) {
    const {
        shouldDisableCell,
        validationResult,
        question,
        showSoftPrompt,
        validateSignal,
        row,
        column,
    } = props;
    const dispatch = useDispatch();
    const cellQuestion = questionForCell(question, row, column);
    const fieldIdentifier = `${question.id}&${cellQuestion.name}`;

    let isDisabled = false;
    let stateDisabledStatus;
    const isHybridGrid = question.type === QuestionType.HybridGrid;

    if (isHybridGrid) {
        stateDisabledStatus = GetQuestionFieldStatus(fieldIdentifier);

        if (stateDisabledStatus) {
            isDisabled = stateDisabledStatus.isDisabled;
        } else {
            isDisabled = shouldDisableCell && shouldDisableCell(row, column);
        }
    } else {
        isDisabled = shouldDisableCell && shouldDisableCell(row, column);
    }

    React.useEffect(() => {
        if (isHybridGrid && stateDisabledStatus === undefined) {
            dispatch(setQuestionFieldStatus([fieldIdentifier], isDisabled));
        }
    }, []); // Runs once per component(Grid Cell)

    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            key={column.index}
            validationResult={validationResult}
            row={row}
            column={column}
        >
            <QuestionContext.Provider value={cellQuestion}>
                <ControlContainer
                    key={column.index}
                    question={cellQuestion}
                    controlType={controlTypeForColumn(column)}
                    gridRendering
                    showSoftPrompt={showSoftPrompt}
                    validateSignal={validateSignal}
                    isDisabled={isDisabled}
                    executeUpdateForIdAfterSelection={fieldIdentifier}
                />
            </QuestionContext.Provider>
        </GridCellRenderer>
    );
}

function renderVariableCells(props: StyledProps, row: AxisModel, column: AxisModel) {
    const { shouldHideCell, question } = props;
    const shouldSplitColumn = isSplitColumn(column);
    if (shouldHideCell && shouldHideCell(row, column)) {
        if (shouldSplitColumn) {
            const variable = variableForCell(row, column);

            if (variable.choiceList.length > 0) {
                return variable.choiceList.map((choice) => (
                    <GridCellRenderer
                        {...getRendererProps(props)}
                        key={choice.index}
                        validationResult={props.validationResult}
                        row={row}
                        column={column}
                        choice={choice}
                    />
                ));
            }
            const questionVariable = question.variables?.find((v) => v.name === variable.name);
            const colSpan = questionVariable.choiceList?.length;
            return <td colSpan={colSpan} />;
        }

        return (
            <GridCellRenderer
                {...getRendererProps(props)}
                key={column.index}
                validationResult={props.validationResult}
                row={row}
                column={column}
            />
        );
    }
    if (shouldSplitColumn) {
        const choiceCellsKey = `${row.index}_${column.index}`;
        return <ChoiceCells key={choiceCellsKey} {...props} row={row} column={column} />;
    }
    return <VariableCell {...props} row={row} column={column} />;
}

function CellImage(props: StyledProps & { choice: ChoiceModel }) {
    const { choice } = props;
    const imageUrl = useTranslatable(choice, "ImageUrl");
    const validTypes = [QuestionType.ChoiceGrid, QuestionType.NetPromoter];

    if (validTypes.some((t) => t === props.question.type)) {
        if (!isNullOrEmpty(imageUrl)) {
            return <img src={imageUrl} alt={choice.name} />;
        }
    }

    return null;
}

function CellItems(props: StyledProps & { column: AxisModel; choice: ChoiceModel }) {
    const { question, column, choice } = props;
    const isNetPromoter = question.type === QuestionType.NetPromoter;
    const items = (
        <>
            <ChoiceColumnOpenEnd choice={choice} column={column} {...props} />
            <CellImage {...props} choice={choice} />
        </>
    );
    return isNetPromoter ? <p>{items}</p> : items;
}

function renderChoiceTextCells(props: StyledProps, row: AxisModel, column: AxisModel) {
    const { question } = props;

    if (isSplitColumn(column)) {
        const choiceList = column.variables.find((v) => v.choiceList?.length > 0)?.choiceList;

        if (!choiceList) {
            const questionVariable = question.variables?.find((v) => v.choiceList?.length > 0);
            const colSpan = questionVariable?.choiceList?.length;

            return <th colSpan={colSpan} key={column.index} />;
        }
        return choiceList?.map((choice) => (
            <GridCellRenderer
                {...getRendererProps(props)}
                key={choice.index}
                isChoiceHeader
                validationResult={props.validationResult}
                row={row}
                column={column}
                choice={choice}
            >
                <ChoiceText {...props} choice={choice} />
                <CellItems {...props} column={column} choice={choice} />
                <SignalListener
                    signal={props.validateSignal}
                    handler={() => props.validateChoiceOpenEnd(column, choice)}
                />
            </GridCellRenderer>
        ));
    }
    const { validationResult } = props;
    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            key={column.index}
            isChoiceHeader
            validationResult={validationResult}
            row={row}
            column={column}
        >
            {row.type === RowType.Text ? (
                <Text {...props} column={column} row={row} index={0} />
            ) : (
                <Title {...props} column={column} row={row} />
            )}
        </GridCellRenderer>
    );
}

function renderTextCells(props: StyledProps, row: AxisModel, column: AxisModel) {
    const { question } = props;
    const cellVariable = column.variables[0];
    const shouldSplitColumn = isSplitColumn(column);
    if (row.type === RowType.Text && shouldSplitColumn) {
        return cellVariable.choiceList.map((choice) => (
            <GridCellRenderer
                {...getRendererProps(props)}
                key={choice.index}
                isColumnHeader
                validationResult={props.validationResult}
                row={row}
                column={column}
                choice={choice}
            >
                <Text {...props} column={column} row={row} index={choice.index} />
            </GridCellRenderer>
        ));
    }

    let cellVariableDefaultChoiceListLength = cellVariable?.choiceList?.length;

    if (!column.variables.every((e) => e.choiceList?.length === 0)) {
        cellVariableDefaultChoiceListLength = question.variables?.find(
            (v) => v.name === cellVariable.name,
        ).choiceList?.length;
    }

    const colSpan = shouldSplitColumn && cellVariable ? cellVariableDefaultChoiceListLength : 1;
    if (colSpan === 0) {
        return null;
    }

    const { validationResult } = props;
    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            key={column.index}
            colSpan={colSpan}
            isColumnHeader
            validationResult={validationResult}
            row={row}
            column={column}
        >
            {row.type === RowType.Text ? (
                <Text {...props} column={column} row={row} index={0} />
            ) : (
                <Title {...props} column={column} row={row} />
            )}
        </GridCellRenderer>
    );
}

function MergedCells(props: StyledProps & { row: AxisModel; column: AxisModel }) {
    const { columns, validationResult, row, column } = props;
    let colSpan = getColumnCount(columns);
    if (!shouldShowRowLeftLabel(row)) {
        colSpan += 1;
    }
    if (!shouldShowRowRightLabel(row)) {
        colSpan += 1;
    }
    if (column.index > 0) {
        return null;
    }
    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            key={column.index}
            colSpan={colSpan}
            isColumnHeader
            validationResult={validationResult}
            row={row}
            column={column}
        >
            {row.type === RowType.Text ? (
                <Text {...props} column={column} row={row} index={0} />
            ) : (
                <Title {...props} column={column} row={row} />
            )}
        </GridCellRenderer>
    );
}

function StandardRow(
    props: StyledProps & { row: AxisModel; showLeftLabels: boolean; showRightLabels: boolean },
) {
    const {
        classes,
        columns,
        validationResult,
        row,
        question,
        isScreenSmall,
        showLeftLabels,
        showRightLabels,
    } = props;
    const errorClassName = rowHasError(row, columns, validationResult) ? classes.gridRowError : "";
    const style = { display: isScreenSmall ? "block" : undefined };
    return (
        <tr className={classnames(classes.gridRow, errorClassName)} role="row" style={style}>
            {showLeftLabels && <RowLeftLabel {...props} row={row} />}
            {columns.map((column) => {
                if (row.type === RowType.Variable) {
                    return (
                        <QuestionContext.Provider
                            key={`${row.index}_${column.index}`}
                            value={questionForCell(question, row, column)}
                        >
                            {renderVariableCells(props, row, column)}
                        </QuestionContext.Provider>
                    );
                }
                if (isRowMergeCells(row)) {
                    return (
                        <MergedCells
                            key={`${row.index}_${column.index}`}
                            {...props}
                            row={row}
                            column={column}
                        />
                    );
                }
                if (getUseChoiceTexts(row)) {
                    return renderChoiceTextCells(props, row, column);
                }
                return renderTextCells(props, row, column);
            })}
            {showRightLabels && <RowRightLabel {...props} row={row} />}
        </tr>
    );
}

function TotalCell(props: StyledProps & { column: AxisModel }) {
    const { column, question, validationResult, getColumnRunningTotal } = props;
    const theme = useTheme();
    const classes = useStyle({ theme });
    const leftLabel = useTranslatable(column, "LeftLabel");
    const rightLabel = useTranslatable(column, "RightLabel");

    return (
        <GridCellRenderer
            {...getRendererProps(props)}
            key={column.index}
            validationResult={validationResult}
            column={column}
        >
            <InputWrapper
                size={getSettingString(column, "Size", defaultSize(question))}
                leftLabel={leftLabel}
                rightLabel={rightLabel}
            >
                <div className={classes.gridSummary}>{getColumnRunningTotal(column)}</div>
            </InputWrapper>
        </GridCellRenderer>
    );
}

function ColumnGroup(props: StyledProps) {
    const { columns } = props;
    const defaultWidth = defaultColumnsWidth(props);
    const firstWidth = firstColumnWidth(props) || defaultWidth;
    return (
        <colgroup>
            <col style={{ width: firstWidth }} />
            {times(getColumnCount(columns), (i) => (
                <col key={i} style={{ width: defaultWidth }} />
            ))}
        </colgroup>
    );
}

function TotalRow(props: StyledProps & { showLeftLabel: boolean }) {
    const { columns, question, validationResult, showLeftLabel } = props;
    return (
        <tr key="total" role="row">
            {showLeftLabel && (
                <GridCellRenderer
                    {...getRendererProps(props)}
                    isRowHeader
                    validationResult={validationResult}
                >
                    <RecalledLabel translatable={question} translatableKey="TotalRowLabel" />
                </GridCellRenderer>
            )}
            {columns.map((column) => (
                <TotalCell key={column.index} {...props} column={column} />
            ))}
        </tr>
    );
}

function StandardRowTree(props: StyledProps) {
    const { rows, question, language } = props;
    const showLeftLabels = hasLeftLabels([...rows, question], language);
    const showRightLabels = hasRightLabels(rows, language);
    const standardRows = rows.map((row) => (
        <StandardRow
            key={row.index}
            {...props}
            row={row}
            showLeftLabels={showLeftLabels}
            showRightLabels={showRightLabels}
        />
    ));
    return (
        <>
            {standardRows}
            {question.type === QuestionType.RunningTotal && (
                <TotalRow key="total" {...props} showLeftLabel={showLeftLabels} />
            )}
        </>
    );
}

function TransposedHeaderRow(props: StyledProps) {
    const { rows } = props;
    return (
        <tr key="header" role="row">
            {rows.map((row) => (
                <GridCellRenderer
                    key={row.index}
                    {...getRendererProps(props)}
                    isChoiceHeader
                    validationResult={props.validationResult}
                    row={row}
                >
                    <LeftLabel {...props} axis={row} />
                    <RowOpenEnd row={row} {...props} />
                    {hasSetting(row, "OpenEndType") && (
                        <SignalListener
                            signal={props.validateSignal}
                            handler={() => props.validateRowOpenEnd(row)}
                        />
                    )}
                </GridCellRenderer>
            ))}
        </tr>
    );
}

function TransposedCells(props: StyledProps & { column: AxisModel; choice: ChoiceModel }) {
    const { column, choice, rows } = props;
    const dispatch = useDispatch();

    if (!dispatch(evaluateIfChoiceVisible(choice))) {
        return null;
    }
    const cells = rows.map((row) => {
        const variable = variableForCell(row, column);
        if (row.type !== RowType.Variable) {
            return (
                <GridCellRenderer
                    {...getRendererProps(props)}
                    key={row.index}
                    isColumnHeader={false}
                    isRowHeader
                    validationResult={props.validationResult}
                    row={row}
                    column={column}
                    choice={choice}
                >
                    <ChoiceText {...props} choice={choice} />
                    <CellItems {...props} column={column} choice={choice} />
                    <SignalListener
                        signal={props.validateSignal}
                        handler={() => props.validateChoiceOpenEnd(column, choice)}
                    />
                </GridCellRenderer>
            );
        }
        const { validationResult, validateSignal } = props;
        return (
            <GridCellRenderer
                {...getRendererProps(props)}
                key={row.index}
                validationResult={validationResult}
                row={row}
                column={column}
                choice={choice}
            >
                <SignalListener
                    signal={validateSignal}
                    handler={() => props.validateCell(row, column)}
                />
                <Choice row={row} column={column} variable={variable} choice={choice} {...props} />
            </GridCellRenderer>
        );
    });

    return (
        <tr key={choice.index} role="row">
            {cells}
        </tr>
    );
}

function TransposedRowTree(props: StyledProps) {
    const { columns } = props;

    return (
        <>
            <TransposedHeaderRow {...props} />

            {columns.map((column) =>
                column.variables
                    .find((v) => v.choiceList?.length > 0)
                    ?.choiceList.map((choice) => (
                        <TransposedCells
                            key={choice.index}
                            {...props}
                            column={column}
                            choice={choice}
                        />
                    )),
            )}
        </>
    );
}

function onShouldDisableCell(
    props: GridRendererProps,
    row: AxisModel,
    column: AxisModel,
    store: StoreState,
    dispatch: Dispatch<any>,
) {
    const { columns } = props;
    const isCellDisabled = columns
        .filter((sequencedColumn) => sequencedColumn.variables[0])
        .some((sequencedColumn) => {
            const cellVariable = variableForCell(row, sequencedColumn);
            return cellVariable.choiceList.some((choice) => {
                const disabledColumns = getSettingString(choice, "DisableColumns");
                if (!isNullOrEmpty(disabledColumns)) {
                    const mentions = getMentions(store.interview.variables, cellVariable.name);
                    if (mentions.some((m) => m.value === choice.value)) {
                        const isDisabled = disabledColumns
                            .replace(/\s/g, "")
                            .split(",")
                            .find((c) => c === column.index.toString());
                        if (isDisabled) {
                            const currentCell = variableForCell(row, column);
                            const currentMentions = getMentions(
                                store.interview.variables,
                                currentCell.name,
                            );
                            if (currentMentions && currentMentions.length > 0) {
                                dispatch(setVariableMentions(currentCell.name, (m) => m.clear()));
                            }
                            return true;
                        }
                    }
                }
                return false;
            });
        });
    return isCellDisabled;
}

function isChoiceDisabled(
    props: StyledProps & {
        row: AxisModel;
        column: AxisModel;
        variable: VariableModel;
        choice: ChoiceModel;
    },
    mentions: MentionModel[],
    store: StoreState,
    dispatch: Dispatch<any>,
) {
    const { row, column, variable, choice } = props;
    if (onShouldDisableCell(props, row, column, store, dispatch)) {
        return true;
    }

    const visibleChoiceMentions = mentions.filter((m) => isChoiceVisible(m.value, variable));
    const maxAllowedAnswers = parseInt(
        props.max || getSettingString(column, "MaxAllowedAnswers"),
        10,
    );
    return (
        !Number.isNaN(maxAllowedAnswers) &&
        !isChoiceSelected(choice, mentions) &&
        visibleChoiceMentions.length >= maxAllowedAnswers
    );
}

function isChoiceVisible(value: string, variable: VariableModel) {
    return variable.choiceList.some((choice) => choice.value === value);
}

function isChoiceSelected(choice: ChoiceModel, mentions: MentionModel[]) {
    return mentions.some((mention) => mention.value === choice.value);
}

function isCollapsibleExpanded(props: GridRendererProps, row: AxisModel) {
    return props.expandedRowsIndices && props.expandedRowsIndices.includes(row.index);
}

function CollapsibleContent(props: StyledProps & { row: AxisModel }) {
    const { validateSignal, shouldHideCell, columns, classes, language, row } = props;

    const visibleColumns = columns.filter(
        (column) => !shouldHideCell || !shouldHideCell(row, column),
    );
    const choices = visibleColumns.map((column) => {
        const variable = variableForCell(row, column);
        return variable.choiceList.map((choice) => (
            <div key={choice.index} className={classes.choiceList}>
                <Choice
                    row={row}
                    column={column}
                    variable={variable}
                    choice={choice}
                    label={translate(choice, "TEXT", language)}
                    isCollapsible
                    {...props}
                />
            </div>
        ));
    });

    const hasRightLabel = hasRightLabels([row], language);

    return (
        <Collapsible expanded={isCollapsibleExpanded(props, row)}>
            <SignalListener
                key="signal"
                handler={() => props.validateCell(row, visibleColumns[0])}
                signal={validateSignal}
            />
            {choices}
            {hasRightLabel && (
                <div className={classes.collapsibleGridFooter}>
                    <RightLabel {...props} axis={row} />
                </div>
            )}
        </Collapsible>
    );
}

function CollapsibleTitleRow(props: StyledProps & { row: AxisModel }) {
    const { classes, columns, row } = props;
    return (
        <div className={classes.collapsibleGridContainer} key={row.index}>
            <div key={row.index} className={classes.collapsibleGridSeparator}>
                <Title {...props} column={columns[0]} row={row} />
            </div>
        </div>
    );
}

function CollapsibleVariableRow(props: StyledProps & { row: AxisModel }) {
    const { classes, row, expandRow, addAccordionRef } = props;
    const className = classnames(
        classes.collapsibleGridHeader,
        !isCollapsibleExpanded(props, row) && classes.collapsibleGridHeaderCollapse,
    );
    return (
        <div className={classes.collapsibleGridContainer} key={row.index}>
            <div
                className={className}
                ref={(ref) => addAccordionRef && addAccordionRef(row.index, ref)}
                onClick={() => expandRow(row)}
            >
                <LeftLabel {...props} axis={row} />
                <FontAwesomeIcon icon="caret-up" className={classes.collapsibleGridArrow} />
            </div>
            <CollapsibleContent {...props} row={row} />
        </div>
    );
}

function CollapsibleRow(props: StyledProps & { row: AxisModel }) {
    const { row } = props;
    switch (row.type as RowType) {
        case RowType.Title:
            return <CollapsibleTitleRow {...props} row={row} />;
        case RowType.Variable:
            return <CollapsibleVariableRow {...props} row={row} />;
        case RowType.Text:
        default:
            return null;
    }
}

function StandardGrid(props: ReferencedElementProps) {
    const { question, classes, elementRef } = props;
    const rows = isTransposedGrid(props) ? (
        <TransposedRowTree {...props} />
    ) : (
        <StandardRowTree {...props} />
    );
    const isChoiceGrid = question.type === QuestionType.ChoiceGrid;

    return (
        <table
            ref={elementRef as React.Ref<HTMLTableElement>}
            className={classnames(classes.grid, isChoiceGrid && classes.choiceGrid)}
        >
            <ColumnGroup {...props} />
            <tbody className={classnames(isChoiceGrid && classes.gridBody)}>{rows}</tbody>
        </table>
    );
}

function CollapsibleGrid(props: ReferencedElementProps) {
    const { classes, elementRef, rows } = props;

    return (
        <div ref={elementRef as React.Ref<HTMLDivElement>} className={classes.collapsibleGrid}>
            {rows.map((row) => (
                <CollapsibleRow key={row.index} {...props} row={row} />
            ))}
        </div>
    );
}

function ResponsiveChoiceGrid(props: StyledProps) {
    const elementRef: React.Ref<HTMLElement> = React.useRef(null);

    const [testFitElementInitialWidth, setTestFitElementInitialWidth] = React.useState<number>();
    const [shouldCollapse, setShouldCollapse] = React.useState<boolean>();

    const { question, rows, columns } = props;
    const isImageChoiceGrid = question.type === QuestionType.ImageChoiceGrid;

    const onWindowSizeChanged = React.useCallback(() => {
        const wrapper = elementRef.current.closest(".page__wrapper--dynamic");
        setShouldCollapse(
            wrapper && (isImageChoiceGrid || wrapper.clientWidth < testFitElementInitialWidth),
        );
    }, [testFitElementInitialWidth, isImageChoiceGrid]);

    React.useEffect(() => {
        if (!elementRef.current) {
            return;
        }
        const bounds = elementRef.current.getBoundingClientRect();
        const width = bounds.left + bounds.width;
        if (!shouldCollapse && testFitElementInitialWidth !== width) {
            setTestFitElementInitialWidth(width);
        }
    }, [testFitElementInitialWidth, rows, columns, shouldCollapse]);

    windowsResizeListener(onWindowSizeChanged);

    return shouldCollapse ? (
        <CollapsibleGrid {...props} elementRef={elementRef} />
    ) : (
        <StandardGrid {...props} elementRef={elementRef} />
    );
}

function ResponsiveStarRatingGrid(props: StyledProps) {
    const elementRef: React.Ref<HTMLElement> = React.useRef(null);
    const [isScreenSmall, setIsScreenSmall] = React.useState<boolean>();

    const onWindowSizeChanged = () => {
        const wrapper = elementRef.current.closest(".page__wrapper--dynamic");
        setIsScreenSmall(wrapper !== null);
    };

    windowsResizeListener(onWindowSizeChanged);

    return <StandardGrid {...props} isScreenSmall={isScreenSmall} elementRef={elementRef} />;
}

function windowsResizeListener(handler: any) {
    React.useEffect(() => {
        window.addEventListener("resize", handler);
        handler();
        return function cleanup() {
            window.removeEventListener("resize", handler);
        };
    }, [handler]);
}

function ResetButton(props: GridRendererProps) {
    const { onReset, theme } = props;

    const question = useQuestion();
    const defaultClearButtonText = useTranslatable(theme, "QuestionClearButton") || "Reset";
    const clearButtonText = useTranslatable(question, "ResetButtonText") || defaultClearButtonText;

    return <Button size="full" text={useRecall(clearButtonText)} onClick={() => onReset()} />;
}

function Grid(props: StyledProps) {
    const { question, rows, columns } = props;
    if (question.type === QuestionType.StarRatingGrid) {
        return <ResponsiveStarRatingGrid {...props} />;
    }
    const isChoiceGrid = [QuestionType.ChoiceGrid, QuestionType.ImageChoiceGrid].includes(
        question.type,
    );

    const hasColumnOpenEnd =
        columns &&
        columns.length &&
        columns[0].variables.some((variable) => variable.choiceList.some(hasOpenEnd));
    const hasRowOpenEnd = rows.some(hasOpenEnd);
    const isResponsiveChoiceGrid = isChoiceGrid && !(hasRowOpenEnd || hasColumnOpenEnd);

    return isResponsiveChoiceGrid ? (
        <ResponsiveChoiceGrid {...props} />
    ) : (
        <StandardGrid {...props} />
    );
}

export default React.memo(function GridRenderer(props: GridRendererProps) {
    const { question } = props;
    const theme = useTheme();
    const classes = useStyle({ theme });

    const hasResetButton =
        question.type === QuestionType.RunningTotal && shouldDisplayResetButton(props);

    return (
        <>
            <Grid {...props} classes={classes} />
            {hasResetButton && <ResetButton {...props} />}
        </>
    );
});
